const CREATOR_REVENUE_STATUS = Object.freeze({
    PENDING_SETTLEMENT: "PENDING_SETTLEMENT",
    PENDING_AFTER_SALES: "PENDING_AFTER_SALES",
    AVAILABLE: "AVAILABLE",
    FROZEN: "FROZEN",
});

const STATUS_LABELS = Object.freeze({
    PENDING_SETTLEMENT: "待结算",
    PENDING_AFTER_SALES: "待售后",
    AVAILABLE: "可提现",
    FROZEN: "已冻结",
});
const RECOGNITION_LABELS = Object.freeze({
    101: "未选片关闭待核对归属",
    NORMAL_COMPLETION: "正常完成",
    USER_NO_SHOW: "用户爽约责任成立",
});
const LEDGER_LABELS = Object.freeze({
    RECOGNIZED: "收益成立",
    CHECKS_PASSED: "检查完成",
    ENTERED_AFTER_SALES: "进入待售后",
    RELEASED: "释放为可提现",
    FROZEN: "收益冻结",
    UNFROZEN: "收益解冻",
    DEDUCTED: "收益扣减",
    REVERSED: "扣减冲正",
});
const ROLE_META = Object.freeze({
    PHOTOGRAPHY: { roleKey: "photographer", roleLabel: "摄影师" },
    MAKEUP: { roleKey: "makeup", roleLabel: "化妆师" },
});

const asText = (value) => value === null || value === undefined ? "" : String(value).trim();
const asMoney = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
};
const serverTime = (value) => {
    const text = asText(value);
    return text ? Date.parse(`${text.replace(" ", "T")}+08:00`) : 0;
};

const buildCreatorRevenueRoleIndex = (orders = []) => (Array.isArray(orders) ? orders : [])
    .reduce((index, order) => {
        const serviceOrderNo = asText(order && order.serviceOrderNo);
        const type = asText(order && order.serviceType).toUpperCase();
        const normalizedRole = asText(order && order.roleKey).toLowerCase();
        if (serviceOrderNo && ["photographer", "makeup"].includes(normalizedRole)) {
            index[serviceOrderNo] = normalizedRole;
        } else if (serviceOrderNo && ROLE_META[type]) {
            index[serviceOrderNo] = ROLE_META[type].roleKey;
        }
        return index;
    }, {});

const getRoleMeta = (raw, roleIndex) => {
    const indexedRole = roleIndex && roleIndex[asText(raw.serviceOrderNo)];
    const type = asText(raw.serviceType || raw.professionalType).toUpperCase();
    if (indexedRole === "makeup" || ["MAKEUP", "MAKEUP_ARTIST", "2"].includes(type)) return ROLE_META.MAKEUP;
    if (indexedRole === "photographer" || ["PHOTOGRAPHY", "PHOTOGRAPHER", "1"].includes(type)) return ROLE_META.PHOTOGRAPHY;
    return { roleKey: "unknown", roleLabel: "身份待确认" };
};

const normalizeCreatorRevenue = (raw = {}, roleIndex = {}) => {
    const status = asText(raw.status).toUpperCase();
    const recognitionType = asText(raw.recognitionType).toUpperCase();
    return {
        serviceOrderNo: asText(raw.serviceOrderNo),
        ...getRoleMeta(raw, roleIndex),
        totalAmount: asMoney(raw.totalAmount),
        currentAmount: asMoney(raw.currentAmount),
        refundReservedAmount: asMoney(raw.refundReservedAmount),
        availableAmount: asMoney(raw.availableAmount),
        status,
        statusLabel: STATUS_LABELS[status] || "状态待确认",
        recognitionType,
        recognitionLabel: RECOGNITION_LABELS[recognitionType] || "收益来源待确认",
        recognizedAt: asText(raw.recognizedAt),
        settlementDueAt: asText(raw.settlementDueAt),
        afterSalesEligibleAt: asText(raw.afterSalesEligibleAt),
    };
};

const normalizeLedgerEntry = (raw = {}) => {
    const entryType = asText(raw.entryType).toUpperCase();
    const statusBefore = asText(raw.statusBefore).toUpperCase();
    const statusAfter = asText(raw.statusAfter).toUpperCase();
    return {
        entryType,
        entryTypeLabel: LEDGER_LABELS[entryType] || "账本事实",
        transactionAmount: asMoney(raw.transactionAmount),
        balanceBefore: asMoney(raw.balanceBefore),
        balanceAfter: asMoney(raw.balanceAfter),
        statusBefore,
        statusBeforeLabel: statusBefore ? STATUS_LABELS[statusBefore] || "状态待确认" : "尚未成立",
        statusAfter,
        statusAfterLabel: STATUS_LABELS[statusAfter] || "状态待确认",
        reason: asText(raw.reason) || "服务端未提供说明",
        occurredAt: asText(raw.occurredAt),
    };
};

const normalizeCreatorRevenueDetail = (raw = {}, roleIndex = {}) => ({
    revenue: normalizeCreatorRevenue(raw.revenue || {}, roleIndex),
    ledgerEntries: (Array.isArray(raw.ledgerEntries) ? raw.ledgerEntries : [])
        .map(normalizeLedgerEntry)
        .sort((left, right) => serverTime(left.occurredAt) - serverTime(right.occurredAt)),
});

const filterCreatorRevenues = (revenues = [], filters = {}) => {
    const role = asText(filters.role).toLowerCase() || "all";
    const status = asText(filters.status).toUpperCase() || "ALL";
    const keyword = asText(filters.keyword).toLowerCase();
    return (Array.isArray(revenues) ? revenues : []).filter((revenue) => (
        (role === "all" || revenue.roleKey === role)
        && (status === "ALL" || revenue.status === status)
        && (!keyword || revenue.serviceOrderNo.toLowerCase().includes(keyword))
    ));
};

module.exports = {
    CREATOR_REVENUE_STATUS,
    buildCreatorRevenueRoleIndex,
    filterCreatorRevenues,
    normalizeCreatorRevenue,
    normalizeCreatorRevenueDetail,
};
